import {
  KARAOKE_TRACK_NUMBER,
  LAYOUT_TRACKS_BASE,
} from '@src/editor/constants';
import { AudioElement } from '@src/editor/models/elements/AudioElement';
import { LogManager } from '@src/utility/Logger';

import { dubPlanIsEmpty, planDubComposition } from './DubEngine';
import { planDubPlacement } from './DubPlacementService';
import { resolveHeardDub } from './heardDub';
import { heardLeadIn } from './leadIn';
import { readOriginalVideo } from './readOriginalVideo';
import { segmentLevelsFor } from './segmentLevels';

import type { DubSource } from './DubDescriptionStore';
import type {
  DubCompositionPlan,
  DubTrackKind,
  PlannedDubElement,
} from './DubEngine';
import type { DubPlacementPlan } from './DubPlacementService';
import type { DubOptions, Timeline } from '@src/editor/models/Timeline';

const logger = LogManager.getLogger('editor:applyDubToDocument');

/**
 * The dub engine's serialize-time half: it puts the dubbed audio into the
 * document a player or a renderer consumes.
 *
 * It builds those tracks from nothing on every pass, from the three stored
 * options and the description held in memory. It never reads what the document
 * already carries, because the document it is handed is built afresh from a
 * stored timeline that holds no dub audio at all.
 *
 * This is deliberately the same shape as the layout engine. Nothing about the
 * dub is stored on the timeline except three options, so the audio is worked
 * out afresh every time a document is produced and can never drift out of step
 * with a picture that has been trimmed, cut or otherwise changed in length —
 * which is the whole point of PLAT-11511.
 *
 * It runs on both paths, the screen preview and the publish render, with one
 * behaviour: only the language the options name goes into the document, and no
 * other language is placed at all, not even at silence. The render service
 * downloads and decodes every file it is handed and never reads a level, so a
 * dub placed at silence is a download for no sound; and on the preview side
 * there is nothing to keep, because switching back to a language costs nothing
 * now that its description is held in memory.
 *
 * It never fetches. The description it builds from is whatever the holder
 * inside the editor has; if that holds nothing for the language, this pass puts
 * no dub audio in the document, and the description's arrival asks for the next
 * pass.
 */

/**
 * The document's own elements, as the serialisers hand them over: plain JSON,
 * after the layout engine and any track reordering have run.
 */
type ElementJSON = Record<string, any>;

/**
 * Every element in the DOCUMENT that is playing the clip's original recording.
 *
 * Found in the finished document rather than on the timeline, and this is
 * load-bearing. The layout engine runs before the dub engine: on a clip with
 * scenes it hides the original video element and puts a CLONE of it inside a
 * layout composition under an invented identifier. The preview document then
 * drops the hidden original entirely. Matching the identifier the timeline
 * knows would therefore write the level onto an element that is not in the
 * document, and the clip would play its own speech at full volume underneath
 * the dubbed voice.
 *
 * The clone keeps the address it plays from, which is what the test recognises,
 * so it is found wherever the layout engine has put it.
 */
function findOriginalVideoInDocument(
  elements: ElementJSON[],
  isOriginalVideoElement: (probe: unknown) => boolean,
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const add = (id: unknown) => {
    if (typeof id !== 'string' || id === '' || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };

  for (const element of elements) {
    if (!element || !isOriginalVideoElement(element)) continue;

    if (element.type === 'composition' && Array.isArray(element.elements)) {
      // The level belongs on the video inside, not on the container.
      for (const child of element.elements) {
        if (child?.type === 'video') add(child.id);
      }
      continue;
    }
    add(element.id);
  }

  return ids;
}

/**
 * The address the document's picture plays the recording from — the first
 * original video found, inside a layout composition or not. Every original
 * video element plays the same recording, so any one of them answers.
 */
function findOriginalSourceInDocument(
  elements: ElementJSON[],
  isOriginalVideoElement: (probe: unknown) => boolean,
): string | null {
  for (const element of elements) {
    if (!element || !isOriginalVideoElement(element)) continue;
    const video =
      element.type === 'composition' && Array.isArray(element.elements)
        ? element.elements.find((child: ElementJSON) => child?.type === 'video')
        : element;
    if (typeof video?.source === 'string' && video.source !== '') {
      return video.source;
    }
  }
  return null;
}

/**
 * The track numbers the dub's two halves take: one each, and never the same
 * one. A track holds one sound at a time, and the bed runs across a whole kept
 * stretch while the dubbed voice sits inside it, so they cannot share.
 *
 * The pair goes straight on top of the clip's ordinary tracks, which is where
 * it has always gone. What is new is that it may keep climbing past the karaoke
 * track when the room underneath has run out, instead of being squashed onto
 * the last track below it.
 *
 * Two numbers are spoken for. The karaoke track's own number is skipped, since
 * a sound placed there would be drawn as captions. Everything from the layout
 * tracks' base upwards belongs to the layout engine and is out of bounds. Both
 * numbers are read from the constants module. Every number between them is fair
 * game, and a number up there is only taken if some element is already on it —
 * track reordering can push an ordinary element above the karaoke track, so
 * being high is not the same as being free.
 *
 * If two free numbers cannot be found in that whole range, this throws. Handing
 * back one number twice is what this function used to do, and it silently lost
 * half the dub.
 *
 * Beyond the pair, `ownTracks` names the parts that play beside the others
 * and so take a track of their own: the original's pieces when the bed plays
 * under them (the bed makes up what the original is turned down by), and the
 * lead-in voice, which can run on under the dub. A part not named shares the
 * bed's track.
 */
export function pickDubTrackNumbers(
  elements: ElementJSON[],
  ownTracks: readonly ('original' | 'leadIn')[] = [],
): Record<DubTrackKind, number> {
  const occupied = new Set<number>();
  let highestOrdinary = 0;
  for (const element of elements) {
    const track = element?.track;
    if (typeof track !== 'number') continue;
    occupied.add(track);
    if (track < KARAOKE_TRACK_NUMBER && track > highestOrdinary) {
      highestOrdinary = track;
    }
  }

  const wanted = 2 + ownTracks.length;
  const chosen: number[] = [];
  for (
    let candidate = highestOrdinary + 1;
    candidate < LAYOUT_TRACKS_BASE && chosen.length < wanted;
    candidate += 1
  ) {
    if (candidate === KARAOKE_TRACK_NUMBER || occupied.has(candidate)) continue;
    chosen.push(candidate);
  }

  if (chosen.length < wanted) {
    throw new Error(
      `This clip has no room for a dub: fewer than ${wanted} free tracks remain between ${
        highestOrdinary + 1
      } and ${LAYOUT_TRACKS_BASE - 1}.`,
    );
  }

  const own = (kind: 'original' | 'leadIn') => {
    const at = ownTracks.indexOf(kind);
    return at === -1 ? chosen[1] : chosen[2 + at];
  };
  return {
    voice: chosen[0],
    music: chosen[1],
    original: own('original'),
    leadIn: own('leadIn'),
  };
}

/**
 * The identifier a planned element takes in the document.
 *
 * Worked out from what the element plays — the language, the half of the dub,
 * the rendition, and where the piece sits on the clip and in the file — so
 * producing the document twice for an unchanged clip names the same elements
 * both times. The preview player matches elements between documents by
 * identifier, so a fresh identifier on every pass would tear the dub down and
 * build it again on every scrub.
 *
 * Never the element's place in the plan: a re-dub of one line splits the merged
 * piece it sat in, which shifts every later piece's place, and the player would
 * then swap new audio and timing under elements it already holds — glitching
 * playback until a reload. Never the signed address either: it is re-signed
 * every hour for the same audio.
 */
function dubElementId(language: string, spec: PlannedDubElement): string {
  const ms = (sec: number) => Math.round(sec * 1000);
  return [
    'dub',
    spec.kind,
    language,
    spec.renditionId || 'bed',
    ms(spec.time),
    ms(spec.trim_start),
    ms(spec.duration),
  ].join('-');
}

/** Turn one planned element into the JSON the document carries. */
function toElementJSON(
  language: string,
  spec: PlannedDubElement,
  track: number,
  formatTime: boolean,
): ElementJSON {
  const element = new AudioElement({
    id: dubElementId(language, spec),
    track,
    source: spec.source,
    time: spec.time,
    duration: spec.duration,
    trim_start: spec.trim_start,
    // A string, never a number: an audio element's constructor reads a number
    // as a fraction and its update reads one as a percentage, so only a string
    // means the same thing on both paths.
    volume: spec.volume,
    // A lead-in's fades into and out of the mute (`planFadesIntoMute`).
    audio_fade_in: spec.audio_fade_in,
    audio_fade_out: spec.audio_fade_out,
  });
  return element.toJSON(formatTime);
}

/** Write a level onto whichever element in the document carries the identifier. */
function setVolume(
  elements: ElementJSON[],
  elementId: string,
  volume: string,
): void {
  for (const element of elements) {
    if (element?.id === elementId) {
      element.volume = volume;
      return;
    }
    if (element?.type !== 'composition' || !Array.isArray(element.elements)) {
      continue;
    }
    for (const child of element.elements) {
      if (child?.id === elementId) {
        child.volume = volume;
        return;
      }
    }
  }
}

export interface ApplyDubToDocumentInput {
  /** The document's elements, already laid out. Never changed in place. */
  elements: ElementJSON[];
  /** The timeline the document was produced from, for the picture's own spans. */
  timeline: Pick<Timeline, 'allElements'>;
  /** The three stored options, or `null` on a clip that has never been dubbed. */
  options: DubOptions | null;
  /** The holder inside the editor, or `null` on a timeline without one. */
  source: DubSource | null;
  /** Whether times are written as frame-based strings, as the caller asked. */
  formatTime: boolean;
}

/**
 * Put this clip's dub into one document, and return the document's elements.
 *
 * Returns the array it was handed, untouched, whenever there is nothing to
 * place: no holder attached, or no options stored. That is the ordinary case
 * for every clip in the library that has never been dubbed, and it must cost
 * nothing.
 */
export function applyDubToDocument({
  elements,
  timeline,
  options,
  source,
  formatTime,
}: ApplyDubToDocumentInput): ElementJSON[] {
  // Nothing to place.
  if (!source || !options) return elements;

  const voiceMode = options.voiceMode;
  const language = options.language;
  const segmentLevels = segmentLevelsFor(
    options.perLanguageConfiguration,
    language,
  );
  const leadIn = heardLeadIn(voiceMode, options.leadIn);

  // The spans come from the TIMELINE, because they are the clip's kept
  // stretches of the recording measured on the clip's own clock, which only the
  // model carries. The identifiers to write a level onto come from the
  // DOCUMENT, because the layout engine may have replaced the picture with a
  // clone of it under a different identifier — see the note above.
  const { spans } = readOriginalVideo(timeline, probe =>
    source.isOriginalVideoElement(probe),
  );
  const originalVideoElementIds = findOriginalVideoInDocument(elements, probe =>
    source.isOriginalVideoElement(probe),
  );

  // The description is read, never fetched. Nothing held for this language
  // means this pass places no dub audio; its arrival asks for the next pass.
  // The same gate decides whether the captions follow the dub.
  let placement: DubPlacementPlan | null = null;
  const heard = resolveHeardDub({ options, source, spans });
  if (heard) {
    const planned = planDubPlacement({
      manifest: heard.manifest,
      renditionUrls: heard.renditionUrls,
      spans,
      // A lead-in turns the original down per line, so every line is kept in
      // an element of its own, as a line with its own levels is.
      isolatedSegmentIds: new Set(
        leadIn
          ? heard.manifest.segments.map(segment => segment.segmentId)
          : Object.keys(segmentLevels ?? {}),
      ),
    });
    if (planned.ok) {
      placement = planned.value;
    } else {
      // Never a throw: a clip whose spans cannot be read can still deliver
      // correct original speech, and that is the safe direction.
      logger.warn('No dub could be placed on this document', {
        language,
        spanCount: spans.length,
        error: planned.error,
      });
    }
  }

  const planned = planDubComposition({
    placement,
    spans,
    originalVideoElementIds,
    voiceMode,
    musicBedUrl: source.musicBedUrl(),
    leadInVoiceUrl: source.voiceStemUrl(),
    musicMode: options.musicMode,
    voiceVolume: options.voiceVolume,
    originalVolume: options.originalVolume,
    segmentLevels,
    leadIn,
    originalAudioSource:
      segmentLevels || leadIn
        ? findOriginalSourceInDocument(elements, probe =>
            source.isOriginalVideoElement(probe),
          )
        : null,
  });

  if (!planned.ok) {
    logger.warn('Could not plan this document’s dub', {
      language,
      error: planned.error,
    });
    return elements;
  }

  return applyPlan(elements, planned.value, language ?? 'original', formatTime);
}

function applyPlan(
  elements: ElementJSON[],
  plan: DubCompositionPlan,
  language: string,
  formatTime: boolean,
): ElementJSON[] {
  // A pass that decides nothing hands the caller's own array straight back, so
  // a clip whose voice position is Original costs no copying at all.
  if (dubPlanIsEmpty(plan)) return elements;

  // Copied rather than edited in place: the caller's array is the document
  // being built, and a composition's children are the model's own JSON.
  let next: ElementJSON[] = elements.map(element => {
    if (element?.type !== 'composition' || !Array.isArray(element.elements)) {
      return element;
    }
    return {
      ...element,
      elements: element.elements.map((child: ElementJSON) => ({ ...child })),
    };
  });

  for (const change of plan.volumeChanges) {
    setVolume(next, change.elementId, change.volume);
  }

  if (plan.elementsToCreate.length > 0) {
    const kinds = new Set(plan.elementsToCreate.map(spec => spec.kind));
    const tracks = pickDubTrackNumbers(next, [
      ...(kinds.has('music') && kinds.has('original')
        ? (['original'] as const)
        : []),
      ...(kinds.has('leadIn') ? (['leadIn'] as const) : []),
    ]);
    next = [
      ...next,
      ...plan.elementsToCreate.map(spec =>
        toElementJSON(language, spec, tracks[spec.kind], formatTime),
      ),
    ];
  }

  return next;
}
